import { createStore } from 'zustand/vanilla';
import type { RenderNodesMap, DrawingSharedState } from '../types/commontypes';

// ─── Types ─────────────────────────────────────────────────────────
interface ShapeStoreState {
  renderNodes: RenderNodesMap;
  drawState: DrawingSharedState;
  dirtyUuids: Set<string>;
  deletedUuids: Set<string>;
}

interface ShapeStoreActions {
  setRenderNodes: (updater: RenderNodesMap | ((prev: RenderNodesMap) => RenderNodesMap)) => void;
  setDrawState: (updater: DrawingSharedState | ((prev: DrawingSharedState) => DrawingSharedState)) => void;
  markDirty: (uuid: string) => void;
  markDeleted: (uuid: string) => void;
  clearDirtySets: () => void;
}

export type ShapeStore = ShapeStoreState & ShapeStoreActions;

// ─── Store Factory ─────────────────────────────────────────────────

/**
 * symbol+timeframe 쌍마다 독립 store를 생성한다.
 * vanilla store (React 비의존)로 생성하여 class 컴포넌트에서도 getState()로 접근 가능.
 */
export function createShapeStore(initialShapesData: RenderNodesMap = {}) {
  return createStore<ShapeStore>(set => ({
    renderNodes: initialShapesData,
    drawState: {},
    dirtyUuids: new Set<string>(),
    deletedUuids: new Set<string>(),

    setRenderNodes: updater => {
      set(state => {
        const next = typeof updater === 'function' ? updater(state.renderNodes) : updater;
        return { renderNodes: next };
      });
    },

    setDrawState: updater => {
      set(state => ({
        drawState: typeof updater === 'function' ? updater(state.drawState) : updater,
      }));
    },

    markDirty: uuid => {
      set(state => {
        const next = new Set(state.dirtyUuids);
        next.add(uuid);
        return { dirtyUuids: next };
      });
    },

    markDeleted: uuid => {
      set(state => {
        const nextDirty = new Set(state.dirtyUuids);
        nextDirty.delete(uuid); // dirty에서 제거 (삭제 대상은 upsert 불필요)
        const nextDeleted = new Set(state.deletedUuids);
        nextDeleted.add(uuid);
        return { dirtyUuids: nextDirty, deletedUuids: nextDeleted };
      });
    },

    clearDirtySets: () => {
      set({ dirtyUuids: new Set(), deletedUuids: new Set() });
    },
  }));
}
